import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import "./WhitelabelSubmissionsList.scss";

interface WhitelabelSubmission {
  id: string;
  appName: string;
  date: string;
  appNameDisplay: string;
  status: "Submitted" | "Rejected" | "Approved";
  notesCount: number;
}

interface WhitelabelSubmissionsListProps {
  isHIDUser: boolean;
  onBackToForm: () => void;
  onViewSubmission: (submissionId: string) => void;
}

type SortColumn = "date" | "status" | null;
type SortDirection = "asc" | "desc";

const WhitelabelSubmissionsList: React.FC<WhitelabelSubmissionsListProps> = ({
  isHIDUser,
  onBackToForm,
  onViewSubmission,
}) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Static sample data - would be replaced with API data
  const submissionsData: WhitelabelSubmission[] = useMemo(
    () => [
      {
        id: "1",
        appName: "ABC",
        date: "13/06/2025",
        appNameDisplay: "ABC",
        status: "Submitted",
        notesCount: 0,
      },
      {
        id: "2",
        appName: "XYZ",
        date: "13/06/2025",
        appNameDisplay: "2",
        status: "Rejected",
        notesCount: 0,
      },
      {
        id: "3",
        appName: "123",
        date: "13/06/2025",
        appNameDisplay: "3",
        status: "Approved",
        notesCount: 4,
      },
    ],
    []
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <Iconography icon="sort" size={IconographySizes.Small} />;
    }
    return sortDirection === "asc" ? (
      <Iconography icon="chevronUp" size={IconographySizes.Small} />
    ) : (
      <Iconography icon="chevronDown" size={IconographySizes.Small} />
    );
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return submissionsData;

    return [...submissionsData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [submissionsData, sortColumn, sortDirection]);

  const getStatusChip = (status: WhitelabelSubmission["status"]) => {
    let className = "";
    switch (status) {
      case "Submitted":
        className = "status-submitted";
        break;
      case "Rejected":
        className = "status-rejected";
        break;
      case "Approved":
        className = "status-approved";
        break;
    }
    return <Chip label={status} className={`status-chip ${className}`} />;
  };

  const getViewLabel = (notesCount: number) => {
    if (notesCount > 0) {
      return `View (${notesCount})`;
    }
    return "View";
  };

  return (
    <div className="whitelabel-submissions-list-container">
      <div className="submissions-content-wrapper">
        <div className="submissions-header">
          <h2>Whitelabel submissions</h2>
        </div>

        <button
          type="button"
          className="back-link"
          onClick={onBackToForm}
        >
          <Iconography icon="arrowLeft" size={IconographySizes.Small} />
          <span>BACK TO APPLY FOR WHITELABEL</span>
        </button>

        <div className="submissions-table-area">
          <TableContainer component={Paper} className="submissions-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>App name</TableCell>
                  <TableCell>
                    <div
                      className={`sortable-header ${sortColumn === "date" ? "active" : ""}`}
                      onClick={() => handleSort("date")}
                    >
                      Date
                      {getSortIcon("date")}
                    </div>
                  </TableCell>
                  <TableCell>App Name</TableCell>
                  <TableCell>
                    <div
                      className={`sortable-header ${sortColumn === "status" ? "active" : ""}`}
                      onClick={() => handleSort("status")}
                    >
                      Status
                      {getSortIcon("status")}
                    </div>
                  </TableCell>
                  <TableCell>View Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>{submission.appName}</TableCell>
                    <TableCell>{submission.date}</TableCell>
                    <TableCell>{submission.appNameDisplay}</TableCell>
                    <TableCell>{getStatusChip(submission.status)}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="view-link"
                        onClick={() => onViewSubmission(submission.id)}
                      >
                        {getViewLabel(submission.notesCount)}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default WhitelabelSubmissionsList;

